import React, { FunctionComponent, useEffect, useMemo, useState } from "react"
import Seo from "../../../components/Seo/Seo"
import { Route, useHistory, useRouteMatch } from "react-router"
import {
  handleUpdateData,
  get,
  handleDeleteData,
} from "../../../store/data/actions"
import { iArtist } from "../../../definitions/Artists"
import Loader from "../../../components/Loader/Loader"
import { iResponse } from "../../../definitions/interfaces"
import { FormProvider, useForm } from "react-hook-form"
import { iAccess } from "../../../definitions/Access"
import { unstable_batchedUpdates } from "react-dom"
import ArtistOverview from "./Overview"
import ArtistHeader from "../Header"
import ArtistUploads from "./Uploads"
import { store } from "react-notifications-component"
import { notificationOptions } from "../../../api/Definitions"
import Access from "../../../components/Access"
import { ItemWithAccess } from "../../../definitions/types"
// import { errorNotification } from "../../../helpers/notificationFunctions"

interface iArtistParams {
  url: string
}

interface iProps {
  access: iAccess
  item: iArtist
  reloadArtist: () => void
}

const Inspector: FunctionComponent<iProps> = ({
  item,
  access,
  reloadArtist,
}) => {
  const [isLoading, setLoading] = useState(false)
  const match = useRouteMatch<iArtistParams>()
  const history = useHistory()

  const methods = useForm<ItemWithAccess<iArtist>>({
    shouldUnregister: false,
    defaultValues: { ...item, __access: access },
  })
  const {
    formState: { dirtyFields },
  } = methods

  useEffect(() => {
    methods.reset({ ...item, __access: access })
  }, [item, access])

  const onSubmit = (data: ItemWithAccess<iArtist>) => {
    setLoading(true)
    handleUpdateData({
      data,
      fields: dirtyFields as Partial<ItemWithAccess<iArtist>>,
      link: "/v1/artists",
    })
      .then(() => {
        setLoading(false)
        reloadArtist()
        store.addNotification({
          title: "Success!",
          message: "Artist successfully updated!",
          type: "success",
          ...notificationOptions,
        })
      })
      .catch((e) => {
        if (e) {
          if (e.reason === "Nothing changed!") {
            store.addNotification({
              message: e.reason,
              type: "warning",
              ...notificationOptions,
            })
          } else {
            store.addNotification({
              message: e.reason,
              type: "danger",
              ...notificationOptions,
            })
          }
        } else {
          store.addNotification({
            message: "Something went wrong!",
            type: "danger",
            ...notificationOptions,
          })
        }
        setLoading(false)
      })
  }

  const onDelete = () => {
    setLoading(true)
    if (confirm("Are you sure you want to delete this?")) {
      handleDeleteData({
        link: "/v1/artists/" + item._id,
      })
        .then(() => {
          setLoading(false)
          store.addNotification({
            title: "Success!",
            message: "Artist successfully deleted!",
            // message: "teodosii@react-notifications-component",
            type: "success",
            ...notificationOptions,
          })
          history.goBack()
        })
        .catch((e) => {
          if (e) {
            if (e.reason === "Nothing changed!") {
              store.addNotification({
                message: e.reason,
                type: "warning",
                ...notificationOptions,
              })
            } else {
              store.addNotification({
                message: e.reason,
                type: "danger",
                ...notificationOptions,
              })
            }
          } else {
            store.addNotification({
              message: "Something went wrong!",
              type: "danger",
              ...notificationOptions,
            })
          }
          setLoading(false)
        })
    }
  }

  const invokeSubmit = () => {
    methods.handleSubmit((data) => {
      onSubmit(data)
    })()
  }

  return (
    <FormProvider {...methods}>
      <Seo title={`${item.name ? item.name : "Inspector"}  | VR-All-Art`} />

      <>
        <ArtistHeader
          handleSubmit={invokeSubmit}
          handleDelete={onDelete}
          access={access}
          title={item.name || "Inspector"}
          submitText="Update"
          submitDeleteText="Delete"
          isLoading={isLoading}
          cover={item.profileImageId}
          published={!!item.published}
        />
        {isLoading ? (
          <Loader />
        ) : (
          <div className="inspector">
            <form
              onSubmit={methods.handleSubmit((data) => {
                onSubmit(data as ItemWithAccess<iArtist>)
              })}
              className="inspector-form"
            >
              <Route exact path={match.path}>
                <ArtistOverview />
              </Route>
              <Route exact path={match.path + "/uploads"}>
                <ArtistUploads item={item} />
              </Route>
              <Route exact path={match.path + "/access"}>
                <Access access={access} />
              </Route>
              <button type="submit" className="btn-blue d-none">
                Save Changes
              </button>
            </form>
          </div>
        )}
      </>
    </FormProvider>
  )
}

const InspectorLoader: FunctionComponent = () => {
  const [isLoading, setLoading] = useState(true)
  const [access, setAccess] = useState<iAccess>()
  const [item, setItem] = useState<iArtist>()
  const match = useRouteMatch<iArtistParams>()

  const loadArtist = () => {
    Promise.all([
      get<iResponse<iAccess>>({
        link: "/v1/access",
        query: { collection: "Artists", url: match.params.url },
      }),
      get<iResponse<iArtist>>({ link: "/v1/artists/" + match.params.url }),
    ]).then((result) => {
      const [accessResult, dataResult] = result
      unstable_batchedUpdates(() => {
        setItem(dataResult.data)
        setAccess(accessResult.data)
        setLoading(false)
      })
    })
  }

  useMemo(() => {
    loadArtist()
  }, [])

  return isLoading ? (
    <Loader />
  ) : item && access ? (
    <Inspector item={item} access={access} reloadArtist={loadArtist} />
  ) : null
}

export default InspectorLoader
